import { Campaign, MassEditCustomFieldFragment, UsersInfoId } from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import * as React from 'react';
import { DelegationMembersMassEditModal } from '../../components/massEdit/massEditModal';
import { Button } from '../../designSystem/components/button';
import { DeleteModal } from '../../designSystem/components/deleteModal';
import { Dropdown } from '../../designSystem/components/dropdown/dropdown';
import { ItemIcon } from '../../designSystem/components/dropdown/item';
import { Menu } from '../../designSystem/components/dropdown/menu';
import { Trigger } from '../../designSystem/components/dropdown/trigger';
import {
    useUsersInfosMassDelegationDeleteMutation,
    useUsersInfosMassEditMutation,
    useVolunteersRegistrationsAccreditationsValidateMutation
} from '../../generated/graphqlHooks';
import { useHeavent } from '../../hooks/useHeavent';
import { IUseMassActionsResult } from '../../hooks/useMassActions';
import { MassAccreditModal } from '../../volunteers/massAccredit/massAccreditModal';
import { MassCampaignSendModal } from '../../volunteers/massCampaignSend/massCampaignSendModal';
import { MassWishedAccreditationsUpdateModal } from '../../volunteers/massWishedAccreditationsUpdate/massWishedAccreditationsUpdateModal';

interface IDelegationMembersActionsProps {
    campaigns: Array<Pick<Campaign, 'id' | 'name'>>;
    customFields: MassEditCustomFieldFragment[];
    deleteFromEvent: boolean;
    massActions: IUseMassActionsResult<UsersInfoId>;
    showAccreditationsEdit: boolean;
    showAccreditationsValidate: boolean;
    showDelete: boolean;
    showWishedAccreditationsEdit: boolean;

    onMassEditSuccess(): void;
}

export const DelegationMembersActions = (props: IDelegationMembersActionsProps) => {
    const {
        translate,
        params: { organizationId, eventId, delegationId }
    } = useHeavent();
    const { mutate: usersInfosMassEdit } = useUsersInfosMassEditMutation();
    const { mutate: accreditationsValidate } =
        useVolunteersRegistrationsAccreditationsValidateMutation();
    const { mutate: massDelegationDelete } = useUsersInfosMassDelegationDeleteMutation();
    const [isMassEditVisible, setIsMassEditVisible] = React.useState(false);
    const [isMassWishedAccreditationsVisible, setIsMassWishedAccreditationsVisible] =
        React.useState(false);
    const [isMassAccreditVisible, setIsMassAccreditVisible] = React.useState(false);
    const [isMassCampaignSendVisible, setIsMassCampaignSendVisible] = React.useState(false);
    const [isMassDeleteOpen, setIsMassDeleteOpen] = React.useState(false);
    const selectedsInput = props.massActions.getSelectedsInput(null, null);
    const showPlus =
        props.showAccreditationsValidate || isNonEmptyArray(props.campaigns) || props.showDelete;
    const onValidate = async () => {
        await accreditationsValidate({
            eventId,
            delegationId,
            usersInfosIds: selectedsInput.ids as UsersInfoId[]
        });

        props.onMassEditSuccess();
    };
    const onMassDelete = async () => {
        await massDelegationDelete({
            organizationId,
            eventId,
            delegationId,
            selecteds: selectedsInput,
            deleteFromEvent: props.deleteFromEvent
        });

        props.onMassEditSuccess();
    };

    return (
        <>
            <Button
                color="invisible-gray"
                leftIcon="pen"
                onClick={() => {
                    setIsMassEditVisible(true);
                }}
            >
                {translate('_diter_un_champ_64621')}
            </Button>

            {props.showWishedAccreditationsEdit && (
                <Button
                    color="invisible-gray"
                    leftIcon="wand-magic-sparkles"
                    onClick={() => {
                        setIsMassWishedAccreditationsVisible(true);
                    }}
                >
                    {translate('_diter_les_accr_41957')}
                </Button>
            )}

            {props.showAccreditationsEdit && (
                <Button
                    color="invisible-gray"
                    leftIcon="wand-magic-sparkles"
                    onClick={() => {
                        setIsMassAccreditVisible(true);
                    }}
                >
                    {translate('_diter_les_accr_86042')}
                </Button>
            )}

            {showPlus && (
                <Dropdown>
                    <Trigger>
                        <Button color="invisible-gray" rightIcon="chevron-down">
                            {translate('plus_77692')}
                        </Button>
                    </Trigger>

                    <Menu placement="bottom-end">
                        {props.showAccreditationsValidate && (
                            <ItemIcon icon="check" onClick={onValidate}>
                                {translate('valider_les_acc_80801')}
                            </ItemIcon>
                        )}

                        {isNonEmptyArray(props.campaigns) && (
                            <ItemIcon
                                icon="paper-plane"
                                onClick={() => {
                                    setIsMassCampaignSendVisible(true);
                                }}
                            >
                                {translate('SendCampaign')}
                            </ItemIcon>
                        )}

                        {props.showDelete && (
                            <ItemIcon
                                color="red"
                                icon="user-xmark"
                                onClick={() => setIsMassDeleteOpen(true)}
                            >
                                {translate('retirer_de_la_d_64963')}
                            </ItemIcon>
                        )}
                    </Menu>
                </Dropdown>
            )}

            {isMassEditVisible && (
                <DelegationMembersMassEditModal
                    customFields={props.customFields}
                    mutate={(massEdit) =>
                        usersInfosMassEdit({
                            organizationId,
                            eventId,
                            delegationId,
                            massEdit
                        })
                    }
                    selecteds={selectedsInput}
                    onClose={() => {
                        setIsMassEditVisible(false);
                    }}
                    onSuccess={props.onMassEditSuccess}
                />
            )}

            {isMassWishedAccreditationsVisible && (
                <MassWishedAccreditationsUpdateModal
                    delegationId={delegationId}
                    eventId={eventId!}
                    numberOfSelected={props.massActions.numberOfSelected}
                    selecteds={selectedsInput}
                    onClose={() => {
                        setIsMassWishedAccreditationsVisible(false);
                    }}
                    onSuccess={props.onMassEditSuccess}
                />
            )}

            {isMassAccreditVisible && (
                <MassAccreditModal
                    eventId={eventId!}
                    numberOfSelected={props.massActions.numberOfSelected}
                    selecteds={selectedsInput}
                    onClose={() => {
                        setIsMassAccreditVisible(false);
                    }}
                    onSuccess={props.onMassEditSuccess}
                />
            )}

            {isMassCampaignSendVisible && (
                <MassCampaignSendModal
                    campaigns={props.campaigns}
                    canCreate={false}
                    delegationId={delegationId}
                    eventId={eventId}
                    organizationId={organizationId}
                    selecteds={selectedsInput}
                    onClose={() => {
                        setIsMassCampaignSendVisible(false);
                    }}
                    onSuccess={props.onMassEditSuccess}
                />
            )}

            {isMassDeleteOpen && (
                <DeleteModal
                    buttonText={translate('supprimer_43083')}
                    subtext={translate('_tes_vous_sur_d_29974')}
                    text={translate('suppression_de_57689', selectedsInput.ids?.length ?? 0)}
                    onClose={() => setIsMassDeleteOpen(false)}
                    onDelete={onMassDelete}
                />
            )}
        </>
    );
};
